import { useState } from "react";
import { Navigate, NavLink, Outlet, useLocation, useParams } from "react-router-dom";
import { Archive, Building2, PawPrint, ShieldAlert, Store, Tags, User as UserIcon } from "lucide-react";
import { cn } from "@/lib/cn";
import { clientRoutes } from "@/lib/clientRoutes";
import { RISK_DOT, RISK_LABEL } from "@/lib/clientFormat";
import { RECORD_TYPE_PILL, BUSINESS_MODEL_LABEL, legalSurfaceLabel, outletsRelevant } from "@/lib/client360Labels";
import { useClient360 } from "@/hooks/useClients";
import { ClassificationDrawer } from "@/components/clients/ClassificationDrawer";

/**
 * A-Block A2 — Client360 shell (/clients/:holdingId).
 *
 * Renders the persistent chrome (identity header + tab bar) and an <Outlet/> for
 * nested child routes. cCPartyType drives the card variant (organization
 * fop/company · physical_person · unknown). displayName is backend-computed
 * (never edited here). Tabs are gated by tabAvailability (backend default-DENY).
 *
 * FIX-2 — chrome is ONE level on every tab: the tab bar sits directly under the
 * header on all tabs (no per-tab BI strip between them, which made the bar jump
 * vertically when switching tabs). The credit-line + BI strip are CONTENT below
 * the tabs (the Огляд index route renders them in its body; other tabs as needed).
 */
export function Client360Page() {
  const { holdingId } = useParams<{ holdingId: string }>();
  const id = holdingId ?? "";
  const location = useLocation();
  const query = useClient360(id);
  const [classifyOpen, setClassifyOpen] = useState(false);

  if (query.isPending) {
    return <Client360Skeleton />;
  }
  // D-144 П-6 — merged holding: server resolved the canonical survivor (ACL
  // already checked there). Navigate replace, keeping the tab suffix + query,
  // so old deepLinks land on the same tab of the canonical holding.
  if (query.data?.redirectTo) {
    const suffix = location.pathname.replace(clientRoutes.client(id), "");
    return <Navigate to={`${clientRoutes.client(query.data.redirectTo)}${suffix}${location.search}`} replace />;
  }
  if (query.isError || !query.data) {
    return (
      <div className="flex h-full items-center justify-center px-6 py-12">
        <div className="max-w-md space-y-3 text-center">
          <ShieldAlert className="mx-auto h-7 w-7 text-destructive" />
          <h1 className="text-lg font-semibold">Клієнта не знайдено або немає доступу</h1>
          <p className="text-sm text-muted-foreground">Перевірте посилання або права доступу.</p>
          <button onClick={() => query.refetch()} className="rounded-md bg-primary px-3.5 py-2 text-sm font-semibold text-primary-foreground">
            Повторити
          </button>
        </div>
      </div>
    );
  }

  const env = query.data;
  const h = env.header;
  const ta = env.tabAvailability;
  const primaryBm = env.businessProfileSummary?.primaryBusinessModel ?? null;
  // §6 — type-aware icon: заводчик ≠ 🏪 магазин; фізособа → person.
  const Icon = h.recordType === "breeder_person"
    ? PawPrint
    : h.legalIdentityType === "individual"
      ? UserIcon
      : h.badge.icon === "Building2" ? Building2 : Store;
  // §3 — type-aware legal-contour surface label (individual → «Дані особи»).
  const entitiesLabel = legalSurfaceLabel(h.legalIdentityType, h.activeMembers);
  // §3b — «Точки» visibility by record_type + business_model (NOT legalIdentityType=fop).
  const showOutlets = (ta.outlets ?? false) && outletsRelevant(h.recordType, primaryBm, h.legalIdentityType);

  const tabs: Array<{ key: string; label: string; to: string; show: boolean; badge?: number }> = [
    { key: "overview", label: "Огляд", to: clientRoutes.client(id), show: true },
    { key: "entities", label: entitiesLabel, to: clientRoutes.entities(id), show: ta.entities, badge: env.counts.activeMembers },
    { key: "contacts", label: "Контакти", to: clientRoutes.contacts(id), show: ta.contacts },
    { key: "outlets", label: "Точки", to: clientRoutes.outlets(id), show: showOutlets },
    { key: "contracts", label: "Договори", to: clientRoutes.contracts(id), show: ta.contracts, badge: env.counts.contractsCount },
    { key: "activities", label: "Історія", to: clientRoutes.activities(id), show: ta.activities },
    { key: "tasks", label: "Задачі", to: clientRoutes.tasks(id), show: ta.tasks ?? true },
    { key: "complaints", label: "Рекламації", to: clientRoutes.complaints(id), show: ta.complaints, badge: env.counts.complaintsActive },
    { key: "profile", label: "Профіль", to: clientRoutes.profile(id), show: ta.profile },
    // D-146 W2 — 10-й таб: Матеріали (badge = backend activeCount).
    { key: "materials", label: "Матеріали", to: clientRoutes.materials(id), show: ta.materials ?? false, badge: env.counts.materialsCount },
  ];

  return (
    <div className="flex h-full flex-col">
      <header className="border-b px-6 pt-5">
        <div className="flex items-start gap-3">
          <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-primary/10 text-primary">
            <Icon className="h-5 w-5" />
          </div>
          <div className="min-w-0 flex-1">
            <div className="flex flex-wrap items-center gap-2">
              <h1 className="truncate text-xl font-semibold tracking-tight" title={h.titleLabel}>
                {h.displayName || h.name}
              </h1>
              {env.biAvailability.available && env.biStrip.holdingAbc && (
                <span className="shrink-0 rounded bg-success px-1.5 py-0.5 text-[11px] font-semibold text-success-foreground">
                  ABC: {env.biStrip.holdingAbc}
                </span>
              )}
              <span className="shrink-0 rounded-full bg-primary/10 px-2 py-0.5 text-[11px] font-medium text-primary">
                {h.badge.label}{h.type === "holding" ? ` · ${h.activeMembers} юрособи` : ""}
              </span>
              {/* D-140 W7 — archived-client pill (direct-open works; archived ≠ ACL). */}
              {h.archived && (
                <span className="inline-flex shrink-0 items-center gap-1 rounded-full bg-muted px-2 py-0.5 text-[11px] font-medium text-muted-foreground">
                  <Archive className="h-3 w-3" /> Архівний клієнт
                </span>
              )}
              {env.businessProfileSummary?.primaryBusinessModel && (
                <span className="shrink-0 rounded-full bg-success px-2 py-0.5 text-[11px] font-medium text-success-foreground">
                  {BUSINESS_MODEL_LABEL[env.businessProfileSummary.primaryBusinessModel] ?? env.businessProfileSummary.primaryBusinessModel}
                </span>
              )}
              {h.recordType && h.recordType !== "unknown" && (
                <span
                  className="shrink-0 rounded-full bg-muted px-2 py-0.5 text-[11px] font-medium text-muted-foreground"
                  title={h.recordTypeSource === "auto_derived" ? "Класифіковано автоматично — перевірте та підтвердіть через «Класифікація»" : undefined}
                >
                  {RECORD_TYPE_PILL[h.recordType] ?? h.recordType}
                  {h.recordTypeSource === "auto_derived" && <span className="opacity-70"> · авто</span>}
                </span>
              )}
              {/* Persistent classification affordance — open the grain-split drawer
                  for any holding (classified or not). */}
              <button
                type="button"
                onClick={() => setClassifyOpen(true)}
                className="inline-flex shrink-0 items-center gap-1 rounded-full border px-2 py-0.5 text-[11px] font-medium text-muted-foreground hover:bg-accent hover:text-foreground"
              >
                <Tags className="h-3 w-3" /> Класифікація
              </button>
            </div>
            <div className="mt-1 flex flex-wrap items-center gap-2 text-xs text-muted-foreground">
              {h.manager && (
                <span className="flex items-center gap-1"><UserIcon className="h-3 w-3" /> {h.manager}</span>
              )}
              {h.identifier && (<><span>·</span><span>{h.identifier.label}: {h.identifier.value}</span></>)}
              {h.legalForm && (<><span>·</span><span>{h.legalForm}</span></>)}
              {env.biAvailability.available && env.biStrip.holdingRiskStatus && (
                <>
                  <span>·</span>
                  <span className="flex items-center gap-1">
                    <span className={cn("h-2 w-2 rounded-full", RISK_DOT[env.biStrip.holdingRiskStatus])} />
                    Ризик: {RISK_LABEL[env.biStrip.holdingRiskStatus]}
                  </span>
                </>
              )}
            </div>
          </div>
          {/* 3A doctrine §0: NO «+ Активність» / «Дія-Запит» here. Operational CTAs
              («+ Задача» {собі/Follow-up} · «Зафіксувати дзвінок») live on the
              Активності (Timeline) + Задачі tabs via TaskActionBar. */}
        </div>

        {h.classificationPrompt && (
          <div className="mt-3 flex flex-wrap items-center justify-between gap-2 rounded-lg border border-warning-foreground/20 bg-warning px-3 py-2 text-sm text-warning-foreground">
            <span>{h.classificationPrompt}</span>
            <button
              type="button"
              onClick={() => setClassifyOpen(true)}
              className="inline-flex shrink-0 items-center gap-1.5 rounded-md bg-warning-foreground px-2.5 py-1.5 text-xs font-semibold text-white hover:bg-warning-foreground/90"
            >
              <Tags className="h-3.5 w-3.5" /> Класифікувати
            </button>
          </div>
        )}

        <nav className="mt-3 flex gap-1 overflow-x-auto" aria-label="Розділи картки клієнта">
          {tabs.filter((t) => t.show).map((t) => (
            <NavLink
              key={t.key}
              to={t.to}
              end={t.key === "overview"}
              className={({ isActive }) =>
                cn(
                  "-mb-px whitespace-nowrap border-b-2 px-3 py-2 text-sm font-medium transition-colors",
                  isActive ? "border-primary text-foreground" : "border-transparent text-muted-foreground hover:text-foreground",
                )
              }
            >
              {t.label}
              {t.badge != null && t.badge > 0 && (
                <span className="ml-1 text-[11px] text-muted-foreground">({t.badge})</span>
              )}
            </NavLink>
          ))}
        </nav>
      </header>

      <div className="flex-1 overflow-y-auto">
        <Outlet context={env} />
      </div>

      <ClassificationDrawer
        open={classifyOpen}
        holdingId={id}
        header={h}
        businessModel={env.businessProfileSummary?.primaryBusinessModel ?? null}
        onClose={() => setClassifyOpen(false)}
      />
    </div>
  );
}

function Client360Skeleton() {
  return (
    <div className="p-6 space-y-4">
      <div className="flex items-center gap-3">
        <div className="h-11 w-11 animate-pulse rounded-xl bg-muted" />
        <div className="flex-1 space-y-2">
          <div className="h-4 w-1/3 animate-pulse rounded bg-muted" />
          <div className="h-3 w-1/4 animate-pulse rounded bg-muted" />
        </div>
      </div>
      <div className="grid grid-cols-3 gap-2 sm:grid-cols-6">
        {[0, 1, 2, 3, 4, 5].map((i) => <div key={i} className="h-12 animate-pulse rounded-lg bg-muted" />)}
      </div>
      <div className="h-40 animate-pulse rounded-lg bg-muted" />
    </div>
  );
}
